/**
 * moj-doc-groups.ts — Wave 67 (บริการ: แปลและรับรองคำแปลโดยนักแปลขึ้นทะเบียนสำหรับงานศาล/กระทรวงยุติธรรม)
 *
 * แหล่งอ้างอิงที่ตรวจสอบได้เท่านั้น:
 *   - ประมวลกฎหมายวิธีพิจารณาความแพ่ง มาตรา 46 (เอกสารภาษาต่างประเทศต้องมีคำแปลเป็นภาษาไทย)
 *   - สำนักงานศาลยุติธรรม (coj.go.th) — แนวปฏิบัติเรื่องเอกสารและล่าม/คำแปลในคดี
 *   - กระทรวงยุติธรรม (moj.go.th) และกรมบังคับคดี (led.go.th) — งานบังคับคดีและเอกสารประกอบ
 *   - กรมการกงสุล กระทรวงการต่างประเทศ (consular.mfa.go.th) — งานนิติกรณ์และการรับรองคำแปล
 *   - สภาทนายความในพระบรมราชูปถัมภ์ (lawyerscouncil.or.th) — ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร
 *   - HCCH — สถานะอนุสัญญา Apostille ของไทย (src/data/apostille-status.ts)
 *
 * กติกาเนื้อหา: ไม่ระบุราคา ไม่รับประกันผล ไม่สร้างสถิติหรือระยะเวลาราชการขึ้นเอง
 * เงื่อนไขที่ต่างกันตามศาล/หน่วยงาน ให้เขียนเป็น "ผันแปร" พร้อมวิธีตรวจสอบ
 */
import { APOSTILLE_STATUS } from "@/data/apostille-status";

export interface MojDocGroup {
  slug: string;
  titleTh: string;
  titleEn: string;
  summaryTh: string;
  summaryEn: string;
  bodyTh: string[];
  bodyEn: string[];
  documentsTh: string[];
  documentsEn: string[];
  chainTh: string[];
  chainEn: string[];
  checklistTh: string[];
  checklistEn: string[];
  faqsTh: { q: string; a: string }[];
  faqsEn: { q: string; a: string }[];
}

const APOSTILLE_TH =
  `หมายเหตุสถานะกฎหมาย: ประเทศไทยยื่นภาคยานุวัติสารเข้าเป็นภาคีอนุสัญญากรุงเฮก ค.ศ. 1961 (Apostille) ` +
  `เมื่อ ${APOSTILLE_STATUS.accessionDepositTh} และอนุสัญญาจะมีผลบังคับใช้กับประเทศไทยในวันที่ ` +
  `${APOSTILLE_STATUS.entryIntoForceTh} (ที่มา: HCCH) ก่อนถึงวันดังกล่าว เอกสารที่จะนำออกไปใช้ต่างประเทศ ` +
  `ยังต้องผ่านการรับรองนิติกรณ์ที่กรมการกงสุลและสถานทูตปลายทางตามลำดับเดิม`;

const APOSTILLE_EN =
  `Legal status note: Thailand deposited its instrument of accession to the 1961 Hague Apostille Convention on ` +
  `${APOSTILLE_STATUS.accessionDepositEn}; the Convention enters into force for Thailand on ` +
  `${APOSTILLE_STATUS.entryIntoForceEn} (source: HCCH). Until that date, documents leaving Thailand still pass ` +
  `through the Department of Consular Affairs and the destination embassy in the traditional order.`;

export const MOJ_DOC_GROUPS: MojDocGroup[] = [
  {
    slug: "pleadings-and-filings",
    titleTh: "คำคู่ความและเอกสารที่ยื่นต่อศาล",
    titleEn: "Pleadings and documents filed with the court",
    summaryTh:
      "คำฟ้อง คำให้การ คำร้อง คำแถลง และเอกสารแนบท้ายที่ต้องยื่นต่อศาลไทย — กลุ่มที่คำแปลต้องตรงกับต้นฉบับทุกถ้อยคำ เพราะศาลใช้เป็นฐานในการวินิจฉัยประเด็นข้อพิพาท",
    summaryEn:
      "Complaints, defences, motions, statements and their annexes filed with a Thai court — the family where the translation must mirror the source word for word, because the court reads it to frame the issues in dispute.",
    bodyTh: [
      "ประมวลกฎหมายวิธีพิจารณาความแพ่ง มาตรา 46 กำหนดว่าเอกสารที่ทำเป็นภาษาต่างประเทศและจะใช้ในกระบวนพิจารณา ต้องมีคำแปลเป็นภาษาไทยยื่นประกอบ โดยผู้แปลต้องรับรองความถูกต้องของคำแปล ในทางปฏิบัติศาลแต่ละแห่งอาจกำหนดรายละเอียดของแบบคำรับรองต่างกัน จึงควรตรวจสอบกับเจ้าหน้าที่หน้าบัลลังก์หรือทนายความผู้รับผิดชอบสำนวนก่อนยื่นทุกครั้ง",
      "คำคู่ความเป็นเอกสารที่กำหนดกรอบของคดี ถ้อยคำที่แปลคลาดเคลื่อนแม้เพียงคำเดียว เช่น การใช้คำว่า 'ผิดนัด' แทน 'ผิดสัญญา' หรือแปลวันครบกำหนดชำระผิด อาจทำให้ประเด็นข้อพิพาทเปลี่ยนไปทั้งคดี งานกลุ่มนี้จึงต้องแปลด้วยศัพท์กฎหมายไทยที่ตรงกับระบบกฎหมายไทย ไม่ใช่การแปลตามความหมายทั่วไปของคำ",
      "เมื่อคู่ความฝ่ายหนึ่งเป็นชาวต่างชาติ มักต้องแปลสองทาง คือแปลเอกสารต่างประเทศเป็นไทยเพื่อยื่นศาล และแปลคำสั่งหรือคำพิพากษาของศาลไทยเป็นภาษาต่างประเทศเพื่อให้ลูกความอ่านเข้าใจหรือใช้ในต่างประเทศ ทั้งสองทิศทางควรใช้อภิธานศัพท์ชุดเดียวกันตลอดคดี เพื่อไม่ให้ชื่อคู่ความ ชื่อสัญญา และคำนิยามในสัญญาถูกแปลไม่ตรงกันคนละฉบับ",
      "ทีมงานของเราแปลโดยนักแปลที่ทำงานเอกสารคดีเป็นประจำ และให้ทนายความในทีมตรวจถ้อยคำทางกฎหมายอีกชั้นก่อนออกใบรับรองคำแปล หากคดีมีวันนัดกำหนดแน่นอน โปรดแจ้งวันนัดมาก่อน เพื่อให้ทีมยืนยันกรอบเวลาที่ทำได้จริงก่อนเริ่มงาน",
      APOSTILLE_TH,
    ],
    bodyEn: [
      "Section 46 of the Thai Civil Procedure Code requires that a document written in a foreign language and used in proceedings be accompanied by a Thai translation, with the translator certifying its accuracy. In practice individual courts may ask for slightly different certificate wording, so confirm the format with the court clerk or with the attorney handling the file before every filing.",
      "Pleadings define the scope of the case. A single mistranslated term — 'default' rendered as 'breach', or a payment due date transposed — can shift the issues the court will decide. This family therefore has to be translated in the Thai legal vocabulary that matches Thai procedure, not in general-purpose language.",
      "Where one party is foreign, translation usually runs both ways: foreign documents into Thai for filing, and Thai orders or judgments into the client's language for review or for use abroad. Both directions should share one glossary for the whole case so party names, contract titles and defined terms never diverge between filings.",
      "Our translators work on court files routinely and an in-house attorney reviews the legal wording before the certificate of accuracy is issued. If a hearing date is already fixed, tell us the deadline first so we can confirm a realistic delivery window before starting.",
      APOSTILLE_EN,
    ],
    documentsTh: [
      "คำฟ้อง คำฟ้องแย้ง และคำให้การ",
      "คำร้อง คำขอ คำแถลง และคำคัดค้าน",
      "บัญชีระบุพยานและเอกสารแนบท้ายคำคู่ความ",
      "หนังสือมอบอำนาจให้ดำเนินคดีและใบแต่งทนายความ",
      "หนังสือบอกกล่าวทวงถามที่อ้างเป็นเอกสารประกอบ",
    ],
    documentsEn: [
      "Complaints, counterclaims and statements of defence",
      "Motions, applications, statements and objections",
      "Lists of evidence and annexes to pleadings",
      "Powers of attorney for litigation and appointment of counsel",
      "Demand letters relied on as supporting documents",
    ],
    chainTh: [
      "รวบรวมต้นฉบับและยืนยันชื่อคู่ความ ชื่อบริษัท และคำนิยามที่ต้องใช้ให้ตรงกันทั้งสำนวน",
      "แปลโดยนักแปลที่ชำนาญเอกสารคดี พร้อมจัดทำอภิธานศัพท์ประจำคดี",
      "ทนายความในทีมตรวจถ้อยคำทางกฎหมายและความครบถ้วนของเอกสารแนบ",
      "ออกคำรับรองความถูกต้องของคำแปลแนบท้ายทุกฉบับ",
      "ยื่นต่อศาลตามรูปแบบที่ศาลนั้นกำหนด (ตรวจสอบกับเจ้าหน้าที่ศาลก่อนยื่น)",
    ],
    chainEn: [
      "Collect the originals and lock the spelling of party names, company names and defined terms across the whole file",
      "Translate with a translator experienced in court documents and build a case glossary",
      "In-house attorney reviews legal wording and checks that every annex is present",
      "Attach a signed certificate of translation accuracy to each document",
      "File in the format that specific court requires (confirm with the court clerk beforehand)",
    ],
    checklistTh: [
      "สแกนต้นฉบับให้ครบทุกหน้า รวมหน้าที่มีตราประทับและลายมือชื่อ",
      "แจ้งเลขคดีและศาลที่ยื่น เพื่อให้ใช้รูปแบบคำรับรองที่ศาลนั้นรับ",
      "แจ้งวันนัดหรือกำหนดยื่นล่วงหน้า",
      "ระบุการสะกดชื่อบุคคลตามหนังสือเดินทางหรือบัตรประชาชน",
    ],
    checklistEn: [
      "Scan every page of the original, including pages with seals and signatures",
      "Give us the case number and the court, so the certificate format matches that court",
      "Tell us the hearing or filing deadline in advance",
      "Confirm the spelling of personal names exactly as in the passport or ID card",
    ],
    faqsTh: [
      {
        q: "ศาลไทยบังคับให้แปลเอกสารภาษาต่างประเทศหรือไม่",
        a: "ตามประมวลกฎหมายวิธีพิจารณาความแพ่ง มาตรา 46 เอกสารภาษาต่างประเทศที่ใช้ในกระบวนพิจารณาต้องมีคำแปลภาษาไทยประกอบ และผู้แปลต้องรับรองความถูกต้อง รายละเอียดของแบบคำรับรองอาจต่างกันในทางปฏิบัติ ควรตรวจสอบกับศาลที่ยื่นก่อน",
      },
      {
        q: "ต้องเป็นนักแปลที่ขึ้นทะเบียนเท่านั้นหรือไม่",
        a: "โดยทั่วไปกฎหมายกำหนดให้ผู้แปลรับรองความถูกต้องของคำแปล แต่บางศาลหรือหน่วยงานอาจกำหนดคุณสมบัติผู้แปลเพิ่มเติม จึงควรยืนยันเงื่อนไขกับศาลหรือหน่วยงานปลายทางก่อนเริ่มงานทุกครั้ง",
      },
      {
        q: "ค่าบริการคิดอย่างไร",
        a: "ขึ้นกับคู่ภาษา จำนวนหน้า และความซับซ้อนของถ้อยคำ จึงประเมินหลังดูเอกสารจริง ส่งไฟล์สแกนมาทางโทรศัพท์ LINE หรืออีเมล แล้วเจ้าหน้าที่จะแจ้งขั้นตอนและค่าบริการกลับไป",
      },
    ],
    faqsEn: [
      {
        q: "Does a Thai court require foreign-language documents to be translated?",
        a: "Under section 46 of the Civil Procedure Code, a foreign-language document used in proceedings must be accompanied by a Thai translation and the translator must certify its accuracy. Certificate wording can vary in practice, so check with the court where you file.",
      },
      {
        q: "Must the translator be officially registered?",
        a: "The general rule is that the translator certifies accuracy, but a particular court or agency may impose extra requirements on who may translate. Confirm the requirement with the receiving court or authority before work starts.",
      },
      {
        q: "How is the fee calculated?",
        a: "Translation work calculations depend on the language pair, page count, and document complexity, which are evaluated after reviewing the files. Clients can send document scans via phone, LINE, or email so our staff can review the material and confirm the necessary processing steps.",
      },
    ],
  },
  {
    slug: "judgments-and-orders",
    titleTh: "คำพิพากษา คำสั่งศาล และหมายต่าง ๆ",
    titleEn: "Judgments, court orders and writs",
    summaryTh:
      "คำพิพากษา คำสั่งศาล หมายเรียก หมายบังคับคดี และคำสั่งคุ้มครองชั่วคราว — เอกสารที่มักต้องแปลเพื่อนำไปใช้กับหน่วยงานอื่นหรือในต่างประเทศ",
    summaryEn:
      "Judgments, court orders, summonses, writs of execution and interim orders — documents usually translated so they can be used before another agency or abroad.",
    bodyTh: [
      "คำพิพากษาและคำสั่งศาลเป็นเอกสารที่หน่วยงานปลายทางอ่านเพื่อดูผลผูกพันทางกฎหมาย คำแปลจึงต้องรักษาโครงสร้างของคำวินิจฉัยไว้ครบ ทั้งส่วนข้อเท็จจริง ประเด็นที่ต้องวินิจฉัย เหตุผล และคำบังคับ การตัดทอนเฉพาะ 'ส่วนสรุป' มักทำให้เอกสารถูกตีกลับ เพราะหน่วยงานไม่สามารถตรวจสอบที่มาของคำบังคับได้",
      "หากต้องนำคำพิพากษาไปใช้ในต่างประเทศ ปกติจะต้องขอคัดสำเนาคำพิพากษาที่รับรองโดยศาลก่อน แล้วจึงแปลและรับรองคำแปล ก่อนเข้าสู่ขั้นตอนรับรองนิติกรณ์ที่กรมการกงสุลและสถานทูตปลายทาง เงื่อนไขว่าปลายทางต้องการเอกสารรูปแบบใดเป็นเรื่องผันแปรตามประเทศ ควรสอบถามหน่วยงานหรือสถานทูตปลายทางให้ชัดเจนก่อนดำเนินการ",
      "ในทางกลับกัน คำพิพากษาของศาลต่างประเทศที่จะนำมาใช้ในไทย ต้องแปลเป็นภาษาไทยพร้อมคำรับรองความถูกต้อง และผลทางกฎหมายว่าจะนำมาใช้ได้เพียงใดเป็นดุลพินิจของศาลไทยตามกฎหมายที่เกี่ยวข้อง เราไม่รับรองผลของคดี แต่รับผิดชอบความถูกต้องของคำแปลและความครบถ้วนของชุดเอกสาร",
      APOSTILLE_TH,
    ],
    bodyEn: [
      "A judgment or court order is read by the receiving authority to establish binding legal effect, so the translation must preserve the full structure of the decision: facts, issues, reasoning and the operative order. Translating only the 'conclusion' is a common reason documents are rejected, because the authority cannot trace how the order was reached.",
      "To use a Thai judgment abroad you normally first obtain a court-certified copy, then have it translated and certified, then move on to legalization at the Department of Consular Affairs and the destination embassy. What the destination actually accepts varies by country, so confirm the requirement with that authority or embassy before you start.",
      "In the other direction, a foreign judgment to be used in Thailand must be translated into Thai with a certificate of accuracy. Whether and how far it can be relied on is for the Thai court to decide under the applicable law. We do not promise case outcomes; we are responsible for translation accuracy and for a complete document set.",
      APOSTILLE_EN,
    ],
    documentsTh: [
      "คำพิพากษาและคำสั่งของศาลไทยทุกชั้นศาล",
      "คำพิพากษาของศาลต่างประเทศและคำสั่งอนุญาโตตุลาการ",
      "หมายเรียก หมายนัด และหมายบังคับคดี",
      "คำสั่งคุ้มครองชั่วคราวและคำสั่งอายัด",
      "หนังสือรับรองคดีถึงที่สุด",
    ],
    documentsEn: [
      "Judgments and orders of Thai courts at every instance",
      "Foreign judgments and arbitral awards",
      "Summonses, hearing notices and writs of execution",
      "Interim protective orders and attachment orders",
      "Certificates that a judgment is final",
    ],
    chainTh: [
      "ขอคัดสำเนาคำพิพากษาหรือคำสั่งที่ศาลรับรองความถูกต้อง",
      "แปลทั้งฉบับ ไม่ตัดทอนส่วนเหตุผลและคำบังคับ",
      "ทนายความในทีมตรวจความถูกต้องของถ้อยคำและเลขคดี",
      "ออกคำรับรองความถูกต้องของคำแปล",
      "หากใช้ต่างประเทศ ดำเนินการนิติกรณ์ที่กรมการกงสุลและยื่นสถานทูตปลายทางตามเงื่อนไขของประเทศนั้น",
    ],
    chainEn: [
      "Obtain a court-certified copy of the judgment or order",
      "Translate the document in full, without dropping the reasoning or the operative part",
      "In-house attorney checks wording, case numbers and dates",
      "Issue the certificate of translation accuracy",
      "For overseas use, complete consular legalization and the destination embassy step as that country requires",
    ],
    checklistTh: [
      "ตรวจว่าสำเนาที่ใช้เป็นฉบับที่ศาลรับรอง ไม่ใช่สำเนาถ่ายเอกสารธรรมดา",
      "แจ้งประเทศและหน่วยงานปลายทางที่จะยื่น",
      "ตรวจการสะกดชื่อคู่ความให้ตรงกับหนังสือเดินทาง",
      "สอบถามปลายทางว่ารับคำแปลรูปแบบใดและต้องรับรองถึงขั้นใด",
    ],
    checklistEn: [
      "Check that your copy is court-certified, not an ordinary photocopy",
      "Tell us the destination country and the receiving authority",
      "Verify party-name spelling against the passport",
      "Ask the destination which translation format it accepts and how far legalization must go",
    ],
    faqsTh: [
      {
        q: "แปลเฉพาะส่วนคำบังคับได้ไหม",
        a: "โดยทั่วไปไม่แนะนำ เพราะหน่วยงานปลายทางมักต้องการเห็นเอกสารทั้งฉบับเพื่อตรวจสอบที่มาของคำบังคับ หากปลายทางระบุชัดว่ารับคำแปลบางส่วนได้ ให้ขอเงื่อนไขเป็นลายลักษณ์อักษรก่อน",
      },
      {
        q: "คำพิพากษาไทยใช้ในต่างประเทศได้ทันทีหรือไม่",
        a: "ไม่เสมอไป การยอมรับผลของคำพิพากษาเป็นไปตามกฎหมายของประเทศปลายทาง และมักต้องผ่านการรับรองเอกสารก่อน ควรตรวจสอบเงื่อนไขกับหน่วยงานหรือสถานทูตปลายทางก่อนเริ่มดำเนินการ",
      },
      {
        q: "ไทยออก Apostille ได้แล้วหรือยัง",
        a: `ยังไม่ได้ในขณะนี้ ไทยยื่นภาคยานุวัติสารเมื่อ ${APOSTILLE_STATUS.accessionDepositTh} และอนุสัญญาจะมีผลบังคับใช้กับไทยในวันที่ ${APOSTILLE_STATUS.entryIntoForceTh} (ที่มา: HCCH) ก่อนถึงวันดังกล่าวยังใช้ขั้นตอนกรมการกงสุลและสถานทูตตามเดิม`,
      },
    ],
    faqsEn: [
      {
        q: "Can we translate only the operative part?",
        a: "Usually not advisable: the receiving authority normally wants the full document to see how the order was reached. If the destination confirms a partial translation is acceptable, get that in writing first.",
      },
      {
        q: "Is a Thai judgment automatically effective abroad?",
        a: "Not necessarily. Recognition follows the law of the destination country and normally requires document legalization first. Check the requirement with the receiving authority or embassy before starting.",
      },
      {
        q: "Can Thailand issue an Apostille yet?",
        a: `Not yet. Thailand deposited its accession on ${APOSTILLE_STATUS.accessionDepositEn} and the Convention enters into force for Thailand on ${APOSTILLE_STATUS.entryIntoForceEn} (source: HCCH). Until then the consular and embassy route still applies.`,
      },
    ],
  },
  {
    slug: "evidence-and-exhibits",
    titleTh: "พยานเอกสารและวัตถุพยานประกอบคดี",
    titleEn: "Documentary evidence and exhibits",
    summaryTh:
      "สัญญา อีเมล ใบแจ้งหนี้ รายงานผู้เชี่ยวชาญ บันทึกการสนทนา และเอกสารอื่นที่อ้างเป็นพยานหลักฐาน — กลุ่มที่ปริมาณมากและต้องคุมศัพท์ให้สม่ำเสมอทั้งชุด",
    summaryEn:
      "Contracts, emails, invoices, expert reports, chat logs and other material relied on as evidence — a high-volume family where terminology must stay consistent across the whole bundle.",
    bodyTh: [
      "พยานเอกสารมักมาเป็นชุดใหญ่และมาจากหลายแหล่ง ทั้งสัญญาฉบับพิมพ์ อีเมล ข้อความแชท ใบเสร็จ และรายงานทางเทคนิค ปัญหาที่พบบ่อยคือคำเดียวกันถูกแปลไม่เหมือนกันในแต่ละไฟล์ ทำให้ฝ่ายตรงข้ามหยิบไปโต้แย้งความน่าเชื่อถือของคำแปลได้ การทำอภิธานศัพท์ประจำคดีตั้งแต่ต้นจึงสำคัญกว่าความเร็ว",
      "เอกสารที่เป็นภาพถ่าย ภาพสแกนคุณภาพต่ำ หรือลายมือเขียน ควรแจ้งล่วงหน้า เพราะต้องใช้เวลาถอดความและอาจต้องระบุในคำแปลว่าส่วนใดอ่านไม่ออก การเดาข้อความที่อ่านไม่ชัดถือเป็นความเสี่ยงต่อความถูกต้องของพยานหลักฐาน เราจะทำหมายเหตุกำกับแทนการเดา",
      "สำหรับข้อความแชทหรืออีเมลจำนวนมาก ควรตกลงรูปแบบการนำเสนอกับทนายความเจ้าของสำนวนก่อน เช่น จะแปลทั้งกระทู้หรือแปลเฉพาะข้อความที่อ้างอิง และจะจัดเรียงตามเวลาแบบใด เพื่อให้ศาลอ่านลำดับเหตุการณ์ได้ต่อเนื่อง",
      APOSTILLE_TH,
    ],
    bodyEn: [
      "Evidence usually arrives as a large bundle from many sources: printed contracts, emails, chat messages, receipts and technical reports. The frequent failure is the same term translated differently in different files, which the other side can use to attack the reliability of the translation. Building a case glossary up front matters more than speed.",
      "Photographs, low-quality scans and handwriting should be flagged in advance: they take transcription time and the translation may need to mark passages that are illegible. Guessing unclear text is a risk to evidentiary accuracy, so we annotate instead of guessing.",
      "For large chat or email sets, agree the presentation format with the attorney on the file first — whole threads or only the cited messages, and how the chronology is ordered — so the court can follow the sequence of events.",
      APOSTILLE_EN,
    ],
    documentsTh: [
      "สัญญา บันทึกข้อตกลง และเอกสารแนบท้ายสัญญา",
      "อีเมล ข้อความแชท และบันทึกการสนทนา",
      "ใบแจ้งหนี้ ใบเสร็จ และเอกสารทางบัญชี",
      "รายงานผู้เชี่ยวชาญและรายงานทางเทคนิค",
      "เอกสารราชการต่างประเทศที่ใช้เป็นพยานหลักฐาน",
    ],
    documentsEn: [
      "Contracts, memoranda of understanding and annexes",
      "Emails, chat messages and conversation logs",
      "Invoices, receipts and accounting records",
      "Expert reports and technical reports",
      "Foreign official documents relied on as evidence",
    ],
    chainTh: [
      "จัดชุดเอกสารและกำหนดเลขอ้างอิงพยานเอกสารให้ตรงกับบัญชีระบุพยาน",
      "จัดทำอภิธานศัพท์ประจำคดีร่วมกับทนายความเจ้าของสำนวน",
      "แปลทั้งชุดโดยคุมศัพท์และรูปแบบวันที่ให้สม่ำเสมอ",
      "ตรวจทานสองรอบและทำหมายเหตุส่วนที่อ่านไม่ออก",
      "ออกคำรับรองความถูกต้องของคำแปลแนบท้ายชุดเอกสาร",
    ],
    chainEn: [
      "Organise the bundle and align exhibit numbers with the list of evidence",
      "Agree a case glossary with the attorney handling the file",
      "Translate the whole set with consistent terminology and date formats",
      "Two review rounds, annotating any illegible passages",
      "Attach a certificate of translation accuracy to the bundle",
    ],
    checklistTh: [
      "ส่งไฟล์ต้นฉบับความละเอียดสูง หรือไฟล์ดิจิทัลต้นทางถ้ามี",
      "แจ้งเลขอ้างอิงพยานเอกสารที่ต้องใช้ในสำนวน",
      "ระบุคำศัพท์เฉพาะทางธุรกิจหรือชื่อผลิตภัณฑ์ที่ต้องคงรูป",
      "แจ้งกำหนดยื่นบัญชีระบุพยานล่วงหน้า",
    ],
    checklistEn: [
      "Send high-resolution originals, or the native digital files if available",
      "Provide the exhibit reference numbers used in the case file",
      "List business terms or product names that must be kept verbatim",
      "Tell us the deadline for filing the list of evidence",
    ],
    faqsTh: [
      {
        q: "เอกสารที่อ่านไม่ชัดจะทำอย่างไร",
        a: "เราจะระบุในคำแปลว่าส่วนนั้นอ่านไม่ออกหรืออ่านได้ไม่ชัด แทนการเดาข้อความ เพื่อรักษาความถูกต้องของพยานหลักฐาน และแจ้งให้ท่านจัดหาต้นฉบับที่ชัดกว่าหากมี",
      },
      {
        q: "จำเป็นต้องแปลทุกหน้าไหม",
        a: "ขึ้นกับแนวทางของทนายความเจ้าของสำนวนและสิ่งที่ศาลต้องการ บางกรณีแปลทั้งฉบับ บางกรณีแปลเฉพาะส่วนที่อ้างอิงพร้อมระบุขอบเขตให้ชัดเจน ควรตกลงกับทนายความก่อนเริ่มงาน",
      },
    ],
    faqsEn: [
      {
        q: "What happens with illegible documents?",
        a: "We mark the passage as illegible or unclear in the translation rather than guessing, to protect evidentiary accuracy, and we ask you for a clearer original if one exists.",
      },
      {
        q: "Does every page have to be translated?",
        a: "The requirement to translate every page depends on your legal counsel's strategy and specific court expectations. Depending on the case, either the entire document is translated or only the cited sections with a clearly defined scope. You should confirm the required translation scope with your attorney before work begins.",
      },
    ],
  },
  {
    slug: "enforcement-and-execution",
    titleTh: "เอกสารบังคับคดีและการยึดอายัดทรัพย์",
    titleEn: "Enforcement and asset execution documents",
    summaryTh:
      "เอกสารในชั้นบังคับคดี ทั้งการยึด อายัด ขายทอดตลาด และการแบ่งชำระหนี้ — กลุ่มที่ตัวเลข ชื่อทรัพย์ และเลขที่เอกสารต้องตรงกันทุกฉบับ",
    summaryEn:
      "Documents in the enforcement stage — seizure, attachment, public auction and distribution of proceeds — where figures, asset descriptions and document numbers must match across every file.",
    bodyTh: [
      "ชั้นบังคับคดีเกี่ยวข้องกับกรมบังคับคดี กระทรวงยุติธรรม เอกสารที่ใช้มักมีตัวเลขจำนวนมาก เช่น ยอดหนี้ ดอกเบี้ย เลขที่โฉนด เลขทะเบียนรถ และเลขบัญชี ความผิดพลาดของตัวเลขเพียงหลักเดียวทำให้เอกสารถูกตีกลับหรือทำให้การอายัดคลาดเคลื่อน คำแปลกลุ่มนี้จึงต้องตรวจตัวเลขซ้ำแบบทีละรายการเทียบกับต้นฉบับ",
      "หากเจ้าหนี้หรือลูกหนี้เป็นชาวต่างชาติหรือเป็นนิติบุคคลต่างประเทศ มักต้องแปลหนังสือรับรองนิติบุคคล หนังสือมอบอำนาจ และเอกสารแสดงสิทธิในทรัพย์สินเพิ่มเติม เงื่อนไขว่าต้องรับรองถึงขั้นใดเป็นเรื่องผันแปรตามหน่วยงานที่รับเรื่อง ควรสอบถามเจ้าหน้าที่ผู้รับผิดชอบสำนวนบังคับคดีก่อนจัดชุดเอกสาร",
      "เราแปลและจัดชุดเอกสารให้พร้อมยื่น พร้อมทำตารางเทียบตัวเลขและชื่อทรัพย์ระหว่างต้นฉบับกับคำแปล เพื่อให้ทนายความและเจ้าหน้าที่ตรวจสอบได้เร็ว โดยไม่รับรองผลของการบังคับคดีซึ่งขึ้นอยู่กับข้อเท็จจริงและดุลพินิจของหน่วยงาน",
      APOSTILLE_TH,
    ],
    bodyEn: [
      "The enforcement stage involves the Legal Execution Department under the Ministry of Justice. These documents are number-heavy: debt balances, interest, title deed numbers, vehicle registrations and bank accounts. A single wrong digit can cause rejection or misdirect an attachment, so this family is checked figure by figure against the source.",
      "Where a creditor or debtor is foreign, company certificates, powers of attorney and proof of ownership usually need translation too. How far those must be certified varies by the office handling the file, so ask the responsible officer before assembling the bundle.",
      "We translate and assemble the bundle ready for filing, with a comparison table of figures and asset descriptions between source and translation so counsel and officers can verify quickly. We do not promise enforcement outcomes, which depend on the facts and on the authority's discretion.",
      APOSTILLE_EN,
    ],
    documentsTh: [
      "คำขอบังคับคดีและคำขอยึดหรืออายัดทรัพย์",
      "บัญชีแสดงรายการทรัพย์และเอกสารแสดงกรรมสิทธิ์",
      "หนังสือรับรองนิติบุคคลและหนังสือมอบอำนาจ",
      "เอกสารประกาศขายทอดตลาด",
      "บัญชีแสดงการรับ-จ่ายเงินและการแบ่งชำระหนี้",
    ],
    documentsEn: [
      "Applications for execution, seizure or attachment",
      "Asset schedules and proof-of-ownership documents",
      "Company certificates and powers of attorney",
      "Public auction notices",
      "Receipt-and-payment accounts and distribution statements",
    ],
    chainTh: [
      "ตรวจสอบชุดเอกสารที่หน่วยงานบังคับคดีต้องการกับเจ้าหน้าที่ผู้รับผิดชอบ",
      "แปลเอกสารพร้อมตรวจตัวเลขและเลขที่เอกสารทีละรายการ",
      "ทนายความในทีมตรวจความสอดคล้องของยอดหนี้และรายการทรัพย์",
      "ออกคำรับรองความถูกต้องของคำแปล",
      "จัดชุดเอกสารพร้อมตารางเทียบตัวเลขสำหรับยื่น",
    ],
    chainEn: [
      "Confirm the required bundle with the officer handling the execution file",
      "Translate and verify every figure and document number item by item",
      "In-house attorney checks that debt totals and asset lists reconcile",
      "Issue the certificate of translation accuracy",
      "Deliver the bundle with a figure-comparison table for filing",
    ],
    checklistTh: [
      "แจ้งเลขคดีและเลขสำนวนบังคับคดี",
      "ส่งเอกสารแสดงกรรมสิทธิ์ฉบับล่าสุด",
      "ยืนยันยอดหนี้และวันที่คิดดอกเบี้ยกับทนายความก่อนแปล",
      "แจ้งกำหนดวันยื่นหรือวันขายทอดตลาด",
    ],
    checklistEn: [
      "Provide the case number and the execution file number",
      "Send the most recent proof-of-ownership documents",
      "Confirm debt totals and interest calculation dates with counsel before translation",
      "Tell us the filing date or auction date",
    ],
    faqsTh: [
      {
        q: "ต้องแปลเอกสารบังคับคดีทั้งชุดหรือไม่",
        a: "ขึ้นกับสิ่งที่หน่วยงานผู้รับเรื่องกำหนด ซึ่งอาจต่างกันตามประเภทของทรัพย์และรูปแบบของสำนวน ควรสอบถามเจ้าหน้าที่ผู้รับผิดชอบสำนวนก่อน แล้วจัดชุดตามที่แจ้ง",
      },
      {
        q: "มีการรับประกันผลการยึดหรืออายัดหรือไม่",
        a: "ไม่มี ผลของการบังคับคดีขึ้นอยู่กับข้อเท็จจริง ทรัพย์สินที่ตรวจพบ และดุลพินิจของหน่วยงาน เราให้บริการด้านคำแปลและการจัดชุดเอกสารให้ถูกต้องครบถ้วนเท่านั้น",
      },
    ],
    faqsEn: [
      {
        q: "Must the whole enforcement bundle be translated?",
        a: "Translating the whole enforcement bundle depends on the specific requirements of the receiving office, which vary based on the asset type and file structure. Individuals should consult the responsible officer first to verify exact translation needs and assemble the document bundle accordingly.",
      },
      {
        q: "Do you guarantee the seizure or attachment result?",
        a: "No. Enforcement outcomes depend on the facts, the assets actually located and the authority's discretion. Our service covers accurate translation and a complete, correctly assembled bundle.",
      },
    ],
  },
  {
    slug: "family-and-status-cases",
    titleTh: "คดีครอบครัวและเอกสารสถานะบุคคล",
    titleEn: "Family cases and personal status documents",
    summaryTh:
      "การหย่า อำนาจปกครองบุตร การรับรองบุตร มรดก และเอกสารทะเบียนราษฎรที่ต้องใช้ประกอบคดี — กลุ่มที่ชื่อ วันเดือนปี และความสัมพันธ์ต้องตรงกันทุกฉบับ",
    summaryEn:
      "Divorce, child custody, legitimation, inheritance and the civil-registration documents that support them — a family where names, dates and relationships must be consistent across every document.",
    bodyTh: [
      "คดีครอบครัวมักใช้เอกสารทะเบียนราษฎรและทะเบียนครอบครัวของกรมการปกครอง เช่น สูติบัตร ทะเบียนสมรส ทะเบียนหย่า ทะเบียนบ้าน และหนังสือรับรองสถานภาพ เมื่อคู่ความฝ่ายหนึ่งเป็นชาวต่างชาติ เอกสารจากต่างประเทศต้องแปลเป็นไทย และเอกสารไทยที่จะใช้ต่างประเทศต้องแปลและผ่านการรับรองตามลำดับ",
      "ปัญหาที่พบบ่อยที่สุดของกลุ่มนี้คือการสะกดชื่อไม่ตรงกันระหว่างหนังสือเดินทาง ทะเบียนสมรส และคำพิพากษา ซึ่งทำให้หน่วยงานปลายทางปฏิเสธเอกสาร เราจึงยึดการสะกดตามหนังสือเดินทางเป็นหลัก และหากเอกสารเดิมสะกดต่างออกไป จะทำหมายเหตุอธิบายไว้ในคำแปลตามข้อเท็จจริง",
      "การจดทะเบียนหย่าหรือบันทึกผลคำพิพากษาในทะเบียนของประเทศปลายทาง มีเงื่อนไขต่างกันในแต่ละประเทศ บางแห่งต้องการคำพิพากษาถึงที่สุดพร้อมหนังสือรับรอง บางแห่งรับเฉพาะเอกสารที่ผ่านการรับรองครบชั้น จึงควรสอบถามสถานทูตหรือหน่วยงานทะเบียนปลายทางก่อนดำเนินการทุกครั้ง",
      APOSTILLE_TH,
    ],
    bodyEn: [
      "Family cases rely on civil- and family-registration documents issued by the Department of Provincial Administration: birth certificates, marriage and divorce registrations, house registration and status certificates. When one party is foreign, foreign documents must be translated into Thai, and Thai documents intended for use abroad must be translated and legalized in order.",
      "The most common failure in this family is inconsistent name spelling between passport, marriage certificate and judgment, which leads the receiving authority to reject the file. We follow the passport spelling as the reference and, where an older document differs, add a factual note in the translation.",
      "Registering a divorce or recording a judgment abroad works differently in each country. Some require a final judgment with a certificate of finality; some accept only fully legalized documents. Always confirm with the destination embassy or registry office before proceeding.",
      APOSTILLE_EN,
    ],
    documentsTh: [
      "คำพิพากษาหย่าและบันทึกข้อตกลงท้ายทะเบียนหย่า",
      "คำสั่งเรื่องอำนาจปกครองบุตรและค่าอุปการะเลี้ยงดู",
      "สูติบัตร ทะเบียนสมรส ทะเบียนหย่า และทะเบียนบ้าน",
      "หนังสือรับรองสถานภาพและหนังสือยินยอม",
      "พินัยกรรมและเอกสารประกอบคดีมรดก",
    ],
    documentsEn: [
      "Divorce judgments and post-divorce agreements",
      "Custody and child-support orders",
      "Birth certificates, marriage and divorce registrations, house registration",
      "Status certificates and letters of consent",
      "Wills and inheritance case documents",
    ],
    chainTh: [
      "ตรวจการสะกดชื่อทุกฉบับให้ตรงกับหนังสือเดินทาง",
      "แปลเอกสารทะเบียนและคำพิพากษาโดยคงรูปแบบวันเดือนปีให้ชัดเจน",
      "ทนายความในทีมตรวจความสัมพันธ์ของบุคคลและความครบถ้วนของชุดเอกสาร",
      "ออกคำรับรองความถูกต้องของคำแปล",
      "หากใช้ต่างประเทศ ดำเนินการรับรองที่กรมการกงสุลและสถานทูตปลายทางตามที่ปลายทางกำหนด",
    ],
    chainEn: [
      "Verify name spelling in every document against the passport",
      "Translate registration documents and judgments with unambiguous date formats",
      "In-house attorney checks family relationships and set completeness",
      "Issue the certificate of translation accuracy",
      "For overseas use, complete consular legalization and the embassy step as the destination requires",
    ],
    checklistTh: [
      "สำเนาหนังสือเดินทางของทุกฝ่ายที่ชื่อปรากฏในเอกสาร",
      "เอกสารทะเบียนฉบับล่าสุดที่คัดจากสำนักทะเบียน",
      "แจ้งประเทศปลายทางและวัตถุประสงค์การใช้เอกสาร",
      "สอบถามปลายทางว่าต้องมีหนังสือรับรองคดีถึงที่สุดหรือไม่",
    ],
    checklistEn: [
      "Passport copies for every person named in the documents",
      "Recently issued registration extracts from the district office",
      "Tell us the destination country and the purpose of use",
      "Ask the destination whether a certificate of finality is required",
    ],
    faqsTh: [
      {
        q: "ชื่อในเอกสารเก่าสะกดไม่ตรงกับหนังสือเดินทางต้องทำอย่างไร",
        a: "เราจะแปลตามข้อเท็จจริงในเอกสารและทำหมายเหตุระบุความต่างของการสะกด พร้อมแนะนำให้ตรวจสอบกับสำนักทะเบียนหรือหน่วยงานปลายทางว่าต้องแก้ไขเอกสารต้นทางก่อนหรือไม่",
      },
      {
        q: "คำพิพากษาหย่าของศาลไทยใช้จดทะเบียนในต่างประเทศได้เลยไหม",
        a: "เป็นเรื่องผันแปรตามกฎหมายและระเบียบของประเทศปลายทาง หลายประเทศต้องการเอกสารที่ผ่านการรับรองครบขั้นตอนก่อน ควรสอบถามสถานทูตหรือหน่วยงานทะเบียนปลายทางเพื่อยืนยันเงื่อนไขล่าสุด",
      },
    ],
    faqsEn: [
      {
        q: "What if an older document spells the name differently from the passport?",
        a: "We translate what the document actually says and add a note explaining the spelling difference, and we advise you to check with the district office or the destination authority whether the source document must be corrected first.",
      },
      {
        q: "Can a Thai divorce judgment be registered abroad directly?",
        a: "Direct overseas registration of a Thai divorce judgment depends on the destination country's specific laws and legal practices. Many foreign jurisdictions require fully legalized documents before accepting the registration. You should confirm current submission requirements with the destination embassy or registry office beforehand.",
      },
    ],
  },
  {
    slug: "corporate-and-arbitration",
    titleTh: "เอกสารนิติบุคคลและข้อพิพาททางธุรกิจ",
    titleEn: "Corporate documents and commercial disputes",
    summaryTh:
      "หนังสือรับรองบริษัท มติที่ประชุม สัญญาธุรกิจ และเอกสารในกระบวนการอนุญาโตตุลาการ — กลุ่มที่ชื่อบริษัทและถ้อยคำในสัญญาต้องคงเส้นคงวาทั้งชุด",
    summaryEn:
      "Company certificates, board resolutions, commercial contracts and arbitration documents — a family where company names and contractual wording must stay uniform across the bundle.",
    bodyTh: [
      "เอกสารนิติบุคคลไทยส่วนใหญ่ออกโดยกรมพัฒนาธุรกิจการค้า กระทรวงพาณิชย์ เช่น หนังสือรับรองบริษัท บัญชีรายชื่อผู้ถือหุ้น และหนังสือบริคณห์สนธิ เมื่อจะใช้ในคดีหรือในต่างประเทศ ควรคัดเอกสารฉบับล่าสุด เพราะหน่วยงานปลายทางหลายแห่งกำหนดอายุของเอกสารที่รับ ซึ่งเป็นเงื่อนไขที่ต่างกันไปในแต่ละแห่ง ควรสอบถามก่อนคัดเอกสาร",
      "งานแปลสัญญาธุรกิจต้องรักษาคำนิยามในสัญญาไว้อย่างเคร่งครัด คำที่ถูกนิยามไว้ในสัญญาต้องถูกแปลด้วยคำเดียวกันตลอดทั้งฉบับและตลอดทั้งชุดเอกสารที่เกี่ยวข้อง หากเอกสารชุดเดียวกันมีทั้งสัญญาหลัก สัญญาแก้ไขเพิ่มเติม และหนังสือบอกกล่าว จะต้องใช้อภิธานศัพท์ร่วมกัน",
      "สำหรับข้อพิพาทที่เข้าสู่กระบวนการอนุญาโตตุลาการ สถาบันแต่ละแห่งมีข้อบังคับเรื่องภาษาของกระบวนพิจารณาและรูปแบบการยื่นเอกสารต่างกัน จึงควรตรวจข้อบังคับของสถาบันที่ใช้และคำสั่งของคณะอนุญาโตตุลาการในคดีนั้นก่อนจัดทำคำแปล เพื่อให้รูปแบบตรงตามที่กำหนด",
      APOSTILLE_TH,
    ],
    bodyEn: [
      "Most Thai corporate documents are issued by the Department of Business Development, Ministry of Commerce: company affidavits, shareholder lists and memoranda of association. For litigation or overseas use, obtain a recently issued extract, because many receiving authorities limit how old a document may be — a requirement that differs by authority, so ask before you order the extract.",
      "Commercial contract translation must protect defined terms. A term defined in the contract has to be rendered with the same wording throughout the document and across every related file, so a bundle containing the main agreement, amendments and notices needs one shared glossary.",
      "For disputes in arbitration, each institution has its own rules on the language of the proceedings and on submission format, and the tribunal may issue procedural directions of its own. Check both before the translation is prepared so the format matches what is required.",
      APOSTILLE_EN,
    ],
    documentsTh: [
      "หนังสือรับรองบริษัทและบัญชีรายชื่อผู้ถือหุ้น",
      "หนังสือบริคณห์สนธิและข้อบังคับบริษัท",
      "รายงานการประชุมและมติคณะกรรมการ",
      "สัญญาธุรกิจ สัญญาแก้ไขเพิ่มเติม และหนังสือบอกกล่าว",
      "คำเสนอข้อพิพาทและเอกสารในกระบวนการอนุญาโตตุลาการ",
    ],
    documentsEn: [
      "Company affidavits and shareholder lists",
      "Memorandum and articles of association",
      "Minutes of meetings and board resolutions",
      "Commercial contracts, amendments and notices",
      "Requests for arbitration and arbitration filings",
    ],
    chainTh: [
      "คัดเอกสารนิติบุคคลฉบับล่าสุดตามที่ปลายทางกำหนด",
      "จัดทำอภิธานศัพท์คำนิยามในสัญญาให้ใช้ร่วมกันทั้งชุด",
      "แปลและตรวจทานโดยนักแปลสายกฎหมายและทนายความในทีม",
      "ออกคำรับรองความถูกต้องของคำแปล",
      "หากใช้ต่างประเทศ ดำเนินการรับรองที่กรมการกงสุลและสถานทูตปลายทางตามเงื่อนไขของประเทศนั้น",
    ],
    chainEn: [
      "Order recently issued corporate extracts as the destination requires",
      "Build a glossary of contractual defined terms shared across the bundle",
      "Translate and review with legal translators and in-house attorneys",
      "Issue the certificate of translation accuracy",
      "For overseas use, complete consular legalization and the embassy step per that country's rules",
    ],
    checklistTh: [
      "ระบุชื่อบริษัทภาษาอังกฤษที่จดทะเบียนไว้ เพื่อไม่ให้แปลชื่อใหม่",
      "ส่งสัญญาหลักและสัญญาแก้ไขเพิ่มเติมพร้อมกันทั้งชุด",
      "แจ้งสถาบันอนุญาโตตุลาการหรือศาลที่จะใช้เอกสาร",
      "แจ้งกำหนดยื่นตามคำสั่งกระบวนพิจารณา",
    ],
    checklistEn: [
      "Give us the registered English company name so it is not re-translated",
      "Send the main agreement together with all amendments",
      "Tell us the arbitral institution or court where the documents will be used",
      "Share the procedural filing deadline",
    ],
    faqsTh: [
      {
        q: "หนังสือรับรองบริษัทต้องคัดใหม่ทุกครั้งหรือไม่",
        a: "หน่วยงานปลายทางหลายแห่งกำหนดอายุของหนังสือรับรองที่รับ ซึ่งไม่เท่ากันทุกแห่ง จึงควรสอบถามเงื่อนไขจากหน่วยงานหรือสถานทูตปลายทางก่อน แล้วจึงคัดเอกสารให้อยู่ในกรอบเวลาที่กำหนด",
      },
      {
        q: "แปลชื่อบริษัทเป็นภาษาอังกฤษเองได้ไหม",
        a: "ควรใช้ชื่อภาษาอังกฤษที่จดทะเบียนไว้กับกรมพัฒนาธุรกิจการค้า ไม่ควรแปลขึ้นใหม่ เพราะชื่อที่ไม่ตรงกับทะเบียนอาจทำให้หน่วยงานปลายทางไม่รับเอกสาร",
      },
    ],
    faqsEn: [
      {
        q: "Does the company affidavit have to be freshly issued each time?",
        a: "Many receiving authorities cap how old the affidavit may be, and the limit is not the same everywhere. Ask the authority or embassy first, then order the extract within that window.",
      },
      {
        q: "Can we translate the company name into English ourselves?",
        a: "Use the English name registered with the Department of Business Development rather than inventing a translation; a name that does not match the register can cause rejection.",
      },
    ],
  },
];

export function getMojDocGroup(slug: string) {
  return MOJ_DOC_GROUPS.find((g) => g.slug === slug);
}
